import { z } from "zod/v4"
import { SubscriptionItemGrantMasterId, ItemId, ItemIdSchema } from "../../id"
import { DocumentSnapshotLike } from "../../firestore"
import { CreatedAt, UpdatedAt } from "../system"
import { PeriodState } from "../common"

export enum SubscriptionItemGrantDistributionStatus {
  Idle = "idle",
  Preparing = "preparing",
  Distributing = "distributing",
  Completed = "completed",
}

export const SubscriptionItemGrantPeriod = z.object({
  start_at: z.number().describe("開始日時 (UNIX Timestamp mills)"),
  end_at: z.number().describe("終了日時 (UNIX Timestamp mills)"),
})

export type SubscriptionItemGrantPeriod = z.infer<typeof SubscriptionItemGrantPeriod>

export const SubscriptionItemGrantMasterNotification = z.object({
  title: z.string().describe("お知らせタイトル"),
  content: z.any().describe("お知らせ本文（QuillDelta形式）"),
})

export type SubscriptionItemGrantMasterNotification = z.infer<typeof SubscriptionItemGrantMasterNotification>

export const SubscriptionItemGrantMasterDocumentMeta = z.object({
  item_ids: z.array(z.string().transform(ItemId)).min(1).describe("配布するアイテムIDのリスト"),
  grant_period: SubscriptionItemGrantPeriod.describe("アイテム付与期間"),
  enabled: z.boolean().describe("有効化フラグ"),
  notification: SubscriptionItemGrantMasterNotification.describe("お知らせ情報"),
  notification_deletion_at: z.number().nullable().describe("お知らせ削除時間"),
})

export type SubscriptionItemGrantMasterDocumentMeta = z.infer<typeof SubscriptionItemGrantMasterDocumentMeta>

export const SubscriptionItemGrantMasterDocumentBody = SubscriptionItemGrantMasterDocumentMeta.extend({
  grant_period_state: PeriodState.describe("アイテム付与期間の状態（システム自動計算）"),
  distribution_status: z.enum(SubscriptionItemGrantDistributionStatus).describe("配布処理のステータス"),
  estimated_total_user_count: z.number().nullable().describe("推定対象ユーザー総数"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type SubscriptionItemGrantMasterDocumentBody = z.infer<typeof SubscriptionItemGrantMasterDocumentBody>

export type SubscriptionItemGrantMasterDocument = SubscriptionItemGrantMasterDocumentBody & {
  id: SubscriptionItemGrantMasterId
}

export class SubscriptionItemGrantMasterDocuments {
  static toDocumentData(doc: SubscriptionItemGrantMasterDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionItemGrantMasterDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SubscriptionItemGrantMasterId(doc.id),
      item_ids: (data["item_ids"] as string[]).map(ItemId),
      grant_period: data["grant_period"],
      grant_period_state: data["grant_period_state"],
      distribution_status: data["distribution_status"] ?? SubscriptionItemGrantDistributionStatus.Idle,
      estimated_total_user_count: data["estimated_total_user_count"] ?? null,
      enabled: data["enabled"],
      notification: data["notification"],
      notification_deletion_at: data["notification_deletion_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * 編集・削除制御
 */
export function canEditOrDeleteMaster(master: SubscriptionItemGrantMasterDocument, now: number): boolean {
  const isBeforePeriod = now < master.grant_period.start_at
  if (isBeforePeriod) return true
  return !master.enabled
}

/**
 * enabled フラグの変更可否
 */
export function canToggleEnabled(master: SubscriptionItemGrantMasterDocument, now: number, newValue: boolean): boolean {
  const isBeforePeriod = now < master.grant_period.start_at
  if (isBeforePeriod) return true
  if (master.enabled) return false
  return newValue === true
}

export const CreateSubscriptionItemGrantMasterRequest = z.object({
  itemIds: z.array(ItemIdSchema).min(1).describe("配布するアイテムIDのリスト"),
  grantPeriod: z.object({
    startAt: z.number().describe("開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().describe("終了日時 (UNIX Timestamp mills)"),
  }).describe("アイテム付与期間"),
  notification: z.object({
    title: z.string().describe("お知らせタイトル"),
    content: z.any().describe("お知らせ本文（QuillDelta形式）"),
  }).describe("お知らせ情報"),
})

export type CreateSubscriptionItemGrantMasterRequest = z.infer<typeof CreateSubscriptionItemGrantMasterRequest>

export const UpdateSubscriptionItemGrantMasterRequest = z.object({
  itemIds: z.array(ItemIdSchema).min(1).optional().describe("配布するアイテムIDのリスト"),
  grantPeriod: z.object({
    startAt: z.number().describe("開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().describe("終了日時 (UNIX Timestamp mills)"),
  }).optional().describe("アイテム付与期間"),
  enabled: z.boolean().optional().describe("有効化フラグ"),
  notification: z.object({
    title: z.string().describe("お知らせタイトル"),
    content: z.any().describe("お知らせ本文（QuillDelta形式）"),
  }).optional().describe("お知らせ情報"),
  notificationDeletionAt: z.number().nullable().optional().describe("お知らせ削除時間"),
})

export type UpdateSubscriptionItemGrantMasterRequest = z.infer<typeof UpdateSubscriptionItemGrantMasterRequest>
